// The tree of the left column (R-U7, R-U24, um/2026-10-30/03 п.1, п.2, п.6, um/2026-10-31/03): sections of records, screens and folders —
// entities only; the views of a section are chosen by the view button of the top line. An icon marks a parent — any node with children,
// at any depth — and the items of the top level; a leaf under a parent hangs on thin branch lines without one. Folders open by hand.

import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { uuidv7, type Filter, type MenuGroup, type MenuItems, type MenuSection, type RecordWords } from '@confdeck/core-shared';
import { useMeta, useQuery, useSync, useTypesTick } from '../sync/react.tsx';
import { Icon } from '../ui/icons.tsx';
import { Button, Chevron, cx, titleIfCut } from '../ui/primitives.tsx';
import { scopeField, useScope, useScopeWords, withScope } from './scope.tsx';
import { pipelineSectionOf } from '../pipelines/pipeline-section.ts';
import { PipelineSectionNav, pipelineLit } from '../pipelines/PipelineSectionNav.tsx';
import { navigate, typeHref } from './router.ts';
import { isOpen, litItem, openOnVisit, pathKeys, typeKey, type NavActive } from './nav-tree.ts';
import { navFields } from './nav-terms.ts';
import { NavTermFolder } from './NavTerms.tsx';
import s from './shell.module.css';

export { navLists, type NavActive } from './nav-tree.ts';
/** The classes of the column's items, for a section that fills the column itself (counts, nodes). */
export const navCss = s;

// Open and closed folders are the person's convenience in this browser (localStorage); the manifest gives the first state.
const KEY = 'confdeck:nav-groups';
const listeners = new Set<() => void>();
let opened: Record<string, boolean> = (() => { try { return JSON.parse(localStorage.getItem(KEY) ?? '{}') as Record<string, boolean>; } catch { return {}; } })();
function setOpened(change: Record<string, boolean>) {
  opened = { ...opened, ...change };
  try { localStorage.setItem(KEY, JSON.stringify(opened)); } catch { /* convenience only */ }
  for (const l of listeners) l();
}
const useOpened = () => useSyncExternalStore((cb) => { listeners.add(cb); return () => listeners.delete(cb); }, () => opened);
/** A folder of a section's own column (the mailboxes of «Почта»): kept open or closed the same way as the folders of the tree. */
export function useFolder(key: string, closedByDefault = false): [boolean, () => void] {
  const open = isOpen(useOpened(), key, closedByDefault);
  return [open, () => setOpened({ [key]: !open })];
}

interface Ctx { section: MenuSection; active: NavActive; lit: ReturnType<typeof litItem>; state: Record<string, boolean>; words: RecordWords }

/** The tree of a section. The way to the open item opens on the first visit only; after that the person decides. */
export function SideNav({ section, active, hideScreens }: { section: MenuSection; active: NavActive; hideScreens?: boolean }) {
  const meta = useMeta();
  const state = useOpened();
  const expandable = (typeId: string) => {
    const t = meta.type(typeId);
    return !!t && navFields(meta, typeId).length > 0;
  };
  const path = pathKeys(section, active, expandable).join('|');
  useEffect(() => {
    const change = openOnVisit(opened, section, active, expandable);
    if (Object.keys(change).length) setOpened(change);
  }, [path]);
  const node = hideScreens ? { ...section, screens: [] } : section;
  const words = useScopeWords();
  return <NodeItems node={node} ctx={{ section, active, lit: litItem(section, active), state, words }} depth={0} />;
}

function NodeItems({ node, ctx, depth }: { node: MenuItems; ctx: Ctx; depth: number }) {
  const meta = useMeta();
  const icon = depth === 0;
  const items: ReactNode[] = [
    // Sections of records first, then views of other types the menu shows as items (the first page of «Билеты» is tickets, um/2026-10-30/05).
    ...node.typeIds.map((tid) => <TypeNav key={tid} typeId={tid} ctx={ctx} depth={depth} />),
    ...(node.views ?? []).map((v) => { const t = meta.type(v.typeId); return t ? (
      <NavRow key={v.viewId} icon={icon ? v.icon : undefined} label={v.label} on={ctx.lit.kind === 'view' && ctx.lit.id === v.viewId} onClick={() => navigate(typeHref(t.key, v.viewId))} />
    ) : null; }),
    // A screen fixed at the bottom of the column (`foot`) is not an item of the tree.
    ...(node.screens ?? []).filter((sc) => !sc.foot && !(ctx.section.eventScoped && ctx.words.screens.has(sc.key))).map((sc) => (
      <NavRow key={sc.key} icon={icon ? sc.icon : undefined} label={sc.label} on={ctx.lit.kind === 'screen' && ctx.lit.id === sc.key} onClick={() => navigate(`/s/${sc.key}`)} />
    )),
    ...(node.groups ?? []).map((g) => <Folder key={g.key} group={g} ctx={ctx} depth={depth} />),
  ].filter(Boolean);
  return depth === 0 ? <>{items}</> : <Branches>{items}</Branches>;
}

/** Children of a parent on branch lines: one vertical line down from the parent and a short horizontal one to each child. */
export function Branches({ children }: { children: ReactNode }) {
  return <div className={s.branches} role="group">{children}</div>;
}

/** One item of the column: an icon only at the top level; the count and the folding arrow at the right edge. */
export function NavRow({ icon, dot, label, on, marked, onClick, count, toggle, title, action, muted }: {
  icon?: string; label: ReactNode; on?: boolean; onClick: () => void; count?: ReactNode; title?: string; muted?: boolean;
  /** A colour in the place of the icon: a pipeline is told by its colour (R-U29). */
  dot?: string;
  /** A choice that narrows the lit item (the pipeline of the deals' view): told by the weight, not by the background — one item is lit. */
  marked?: boolean;
  toggle?: { open: boolean; onToggle: () => void; label: string };
  /** A tool of the item shown under the pointer (the settings of a mailbox, of a pipeline). */
  action?: { icon: string; label: string; onClick: () => void };
}) {
  return (
    <div className={s.navRow}>
      <button type="button" data-nav-label={typeof label === 'string' ? label : title} aria-pressed={marked ?? undefined} className={cx(s.navItem, on && s.navOn, marked && s.navMarked, !icon && !dot && s.navBranch, muted && s.navMuted, (toggle || action) && s.navHasTool)} aria-current={on ? 'page' : undefined} title={title} onClick={onClick}>
        {icon && <Icon name={icon} size={15} />}{!icon && dot && <span className={s.navDot} aria-hidden><i style={{ background: `var(--c-${dot}-dot)` }} /></span>}<span className={s.navLabel} onMouseEnter={title ? undefined : titleIfCut}>{label}</span>{count}
      </button>
      {action && <button type="button" className={s.navTool} aria-label={action.label} title={action.label} onClick={action.onClick}><Icon name={action.icon} size={13} /></button>}
      {toggle && (
        <button type="button" className={s.navToggle} aria-expanded={toggle.open} aria-label={`${toggle.open ? 'Свернуть' : 'Развернуть'} «${toggle.label}»`} onClick={toggle.onToggle}>
          <Chevron dir="right" open={toggle.open} size={13} />
        </button>
      )}
    </div>
  );
}

function Folder({ group, ctx, depth }: { group: MenuGroup; ctx: Ctx; depth: number }) {
  const open = isOpen(ctx.state, group.key, group.collapsed);
  const flip = () => setOpened({ [group.key]: !open });
  return (
    <div className={s.navNode}>
      <NavRow icon={group.icon} label={group.label} onClick={flip} toggle={{ open, onToggle: flip, label: group.label }} />
      {open && <NodeItems node={group} ctx={ctx} depth={depth + 1} />}
    </div>
  );
}

/** The count beside a section: of the chosen event in an event section (audit 28.10, L4), of all records otherwise. */
function NavCount({ typeId, scoped }: { typeId: string; scoped: boolean }) {
  const meta = useMeta();
  const client = useSync();
  const [scopeId] = useScope();
  const field = scoped && scopeId ? scopeField(meta, typeId, client.header?.scope?.typeId) : undefined;
  return field ? <ScopedCount typeId={typeId} filter={withScope(null, field, scopeId)} /> : <span className={s.navCount}>{client.store.count(typeId).toLocaleString('ru-RU')}</span>;
}
function ScopedCount({ typeId, filter }: { typeId: string; filter: Filter | null }) {
  const q = useQuery(typeId, filter, [], '');
  return <span className={s.navCount}>{q.ids.length.toLocaleString('ru-RU')}</span>;
}

/**
 * A section of records: one item that opens its first view (R-U24 — the views are not items of the tree); the values of the fields
 * the owner put into the column hang under it while it is open. A section that lives in pipelines has its own items instead (R-U29).
 */
function TypeNav({ typeId, ctx, depth }: { typeId: string; ctx: Ctx; depth: number }) {
  const meta = useMeta();
  useTypesTick([typeId]);
  const t = meta.type(typeId);
  if (!t) return null;
  const here = ctx.lit.kind === 'type' && ctx.lit.id === typeId;
  const piped = pipelineSectionOf(t.key);
  if (piped) return <PipelineSectionNav typeId={typeId} def={piped} lit={ctx.active.typeId === typeId ? pipelineLit(ctx.lit.kind === 'extra' ? ctx.lit.id : undefined, here) : null} />;
  const fields = navFields(meta, typeId);
  const expandable = fields.length > 0;
  const open = expandable && isOpen(ctx.state, typeKey(typeId), true);
  const flip = () => setOpened({ [typeKey(typeId)]: !open });
  // A section the chosen event narrows is named in its words (R-E9): the tickets of a product without a date are «Доступы».
  const name = (ctx.section.eventScoped ? ctx.words.types.get(typeId)?.nameMany : undefined) ?? t.nameMany;
  return (
    <div className={s.navNode}>
      <NavRow icon={depth === 0 || expandable ? t.icon : undefined} label={name} on={here} onClick={() => navigate(typeHref(t.key))}
        count={<NavCount typeId={typeId} scoped={!!ctx.section.eventScoped} />} toggle={expandable ? { open, onToggle: flip, label: name } : undefined} />
      {open && (
        <Branches>
          {fields.map((f) => <NavTermFolder key={f.id} typeKey={t.key} field={f} lit={ctx.lit.kind === 'extra' && ctx.active.typeId === typeId ? ctx.lit.id : undefined} />)}
        </Branches>
      )}
    </div>
  );
}

/** «Новый справочник»: the owner makes a section of their own (R-M2). Shown in «Справочники» only (um/2026-10-30/03 п.1). */
export function CreateType({ big }: { big?: boolean }) {
  const meta = useMeta();
  const client = useSync();
  const [name, setName] = useState<string | null>(null);
  const isOwner = meta.user(client.header?.userId)?.role === 'owner';
  if (!isOwner) return null;
  return name === null ? (
    big ? <Button variant="primary" icon="plus" onClick={() => setName('')}>Новый справочник</Button>
      : <button type="button" className={cx(s.navItem, s.navMuted)} onClick={() => setName('')}><Icon name="plus" size={15} /><span className={s.navLabel}>Новый справочник</span></button>
  ) : (
    <input autoFocus className={cx(s.newType, big && s.newTypeBig)} placeholder="Например, Площадки" aria-label="Название справочника" value={name} onChange={(e) => setName(e.target.value)} onBlur={() => setName(null)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setName(null);
        if (e.key === 'Enter' && name.trim()) {
          const id = uuidv7();
          const many = name.trim();
          client.mutate('type.create', { id, name: many, nameMany: many, icon: 'book' });
          setName(null);
          const stop = client.store.subscribe(() => { const t = client.store.meta.type(id); if (t) { stop(); navigate(typeHref(t.key)); } });
          setTimeout(stop, 5000);
        }
      }} />
  );
}
